/**
 * Guided wizard — VOICE mode capture screen (Phase B).
 *
 * Steps 2–5 collapse into this ONE screen when mode === "voice":
 *
 *   1. CAPTURE — "Tell Atlas about your load" + big mic (same capture stack
 *      as the Atlas voice card: getUserMedia → MediaRecorder, 90s auto-stop,
 *      m4a/webm handling) → voice-post mode=builder with client_date/client_tz.
 *   2. LOAD-BEING-BUILT CARD — the parsed VoiceBuilderLoad mapped onto the
 *      wizard's WizardFields (voiceLoadToWizard: geocode each stop, resolve
 *      date_text via resolveDatePhrase, equipment via the shared enum map),
 *      rendered as freight structure (ROUTE / DATES / LOAD / NOTES) with the
 *      3-state VoiceFieldShell: captured / missing / needs-confirmation.
 *   3. CORRECT BY TAP OR VOICE — tapping a field swaps in the SAME editor
 *      Type mode uses; a correction mic sends mode=builder_correct with the
 *      current load (stable stop ids) → ops → applyVoiceOps → only the
 *      changed fields update, with "Atlas changed N things" feedback.
 *   4. GATING — blockers (missing required + unresolved confirmations) are
 *      reported to the wizard page via onGateChange; the user cannot reach
 *      Review until the card is clean.
 *
 * The Phase-A brain is consumed, never modified: parse → VoiceBuilderLoad,
 * correct → ops → applyVoiceOps, validate → validateVoiceLoad. The wizard
 * page owns WizardFields and posts via the unchanged saveDirectLoadPost path.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Mic,
  Square,
  Loader2,
  RotateCcw,
  AlertTriangle,
  MapPin,
  CalendarDays,
  Truck,
  StickyNote,
  ChevronDown,
  ListChecks,
  Check,
  Circle,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getLocalTodayYMD, formatYYYYMMDDToLocaleDateString } from "@/lib/dateOnly";
import { LocationAutocomplete, type PlaceData } from "@/components/ui/location-autocomplete";
import { DatePickerField } from "@/components/ui/date-picker-field";
import { EquipmentSelect } from "@/components/ui/equipment-select";
import { FieldInput } from "@/components/post-load/PostLoadCard";
import { ToggleChipRow, EQUIPMENT_CHIPS } from "@/components/post-load/EquipmentDetailsCard";
import { HANDLING_OPTIONS } from "@/components/lr-direct/DirectPublishCard";
import { EQUIPMENT_FLAT_LIST } from "@/lib/equipmentTypes";
import { resolveSpokenCity } from "@/hooks/useVoicePostDirectLoad";
import { useMicVolumeMeter } from "@/hooks/useMicVolumeMeter";
import {
  applyVoiceOps,
  validateVoiceLoad,
  type VoiceLoadConfirmation,
} from "@/lib/lrDirect/applyVoiceOps";
import type { VoiceBuilderLoad, VoiceOp } from "@/lib/lrDirect/voiceBuilderTypes";
import {
  mapVoiceLoadToWizard,
  mapVoiceHandling,
  type VoiceStopDateInfo,
} from "@/lib/lrDirect/voiceLoadToWizard";
import { isReeferEquipment, voiceEquipmentToEnum } from "@/lib/lrDirect/voiceEquipment";
import { isSpokenDatePhrase } from "@/lib/lrDirect/resolveDatePhrase";
import { voiceCues } from "@/lib/lrDirect/voiceCues";
import { cn } from "@/lib/utils";
import { pickupLabel, dropLabel, type WizardFields } from "./wizardTypes";
import { VoiceFieldShell, type VoiceFieldState } from "./VoiceFieldShell";

type CapturePhase = "idle" | "recording" | "processing" | "glow" | "built" | "error";
type RecordPurpose = "build" | "correct";

const MAX_RECORDING_SECONDS = 90; // same cap as the Atlas voice card

/** Silent defaults — weight/length are never "needed", they fall back to these. */
const DEFAULT_WEIGHT_LBS = 41000;
const DEFAULT_LENGTH_FT = 53;
/** Brief glow beat on the record screen before advancing to the built card. */
const GLOW_ADVANCE_MS = 900;

interface WizardVoiceCaptureProps {
  fields: WizardFields;
  onChange: (patch: Partial<WizardFields>) => void;
  postType: "load" | "quote";
  /** Report the current gating blockers to the wizard page (empty = clean). */
  onGateChange: (blockers: string[]) => void;
  /** Escape hatch — switch to Type mode, keeping everything captured so far. */
  onSwitchToType: () => void;
}

/** Defensive shape-normalization of the edge function's builder response. */
function normalizeBuilderLoad(raw: unknown): VoiceBuilderLoad {
  const r = (raw ?? {}) as Record<string, unknown>;
  const stops = (Array.isArray(r.stops) ? r.stops : [])
    .filter((s): s is Record<string, unknown> => !!s && typeof s === "object" && !!(s as any).id)
    .map((s) => ({
      id: String(s.id),
      stop_type: s.stop_type === "drop" ? ("drop" as const) : ("pickup" as const),
      city: String(s.city ?? ""),
      state: String(s.state ?? "").toUpperCase().slice(0, 2),
      // Junk tokens ("null", "none") the AI emits for unspoken dates count as
      // NOT spoken — never surface them as a heard phrase or confirmation.
      date_text: s.date_text != null && isSpokenDatePhrase(String(s.date_text)) ? String(s.date_text) : null,
    }));
  const num = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v) ? Math.round(v) : null;
  return {
    stops,
    equipment: r.equipment ? String(r.equipment) : null,
    weight_lbs: num(r.weight_lbs),
    length_ft: num(r.length_ft),
    commodity: r.commodity ? String(r.commodity) : null,
    temperature_f: num(r.temperature_f),
    handling: (Array.isArray(r.handling) ? r.handling : []).map(String).filter(Boolean),
    notes: r.notes ? String(r.notes) : null,
    ambiguities: (Array.isArray(r.ambiguities) ? r.ambiguities : [])
      .filter((a): a is Record<string, unknown> => !!a && typeof a === "object")
      .map((a) => ({
        field: String(a.field ?? ""),
        heard: String(a.heard ?? ""),
        candidates: (Array.isArray(a.candidates) ? a.candidates : []).map(String),
      })),
  };

}

/** "2026-08-28" → "August 28, 2026" — a phrase the deterministic resolver parses exactly. */
const phraseFromYMD = (ymd: string): string => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d, 12).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const equipmentLabel = (enumValue: string, spoken: string | null): string => {
  const found = EQUIPMENT_FLAT_LIST.find((e) => e.enumValue === enumValue);
  return found?.name ?? spoken ?? enumValue;
};

export default function WizardVoiceCapture({
  fields,
  onChange,
  postType,
  onGateChange,
  onSwitchToType,
}: WizardVoiceCaptureProps) {
  const { toast } = useToast();

  const [phase, setPhase] = useState<CapturePhase>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [transcript, setTranscript] = useState("");
  const [showTranscript, setShowTranscript] = useState(false);
  const [voiceLoad, setVoiceLoad] = useState<VoiceBuilderLoad | null>(null);
  const [stopDates, setStopDates] = useState<Record<string, VoiceStopDateInfo>>({});
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [recordPurpose, setRecordPurpose] = useState<RecordPurpose>("build");
  const [secondsLeft, setSecondsLeft] = useState(MAX_RECORDING_SECONDS);
  /** "Atlas changed N things" feedback after a voice correction. */
  const [lastChange, setLastChange] = useState<{ count: number; notes: string[] } | null>(null);
  /** Live mic stream for the volume meter (Phase C listening animation). */
  const [liveStream, setLiveStream] = useState<MediaStream | null>(null);
  /** Field-fill pulse: tokens name which shells flash after a parse/correction. */
  const [pulse, setPulse] = useState<{ tokens: string[]; nonce: number }>({ tokens: [], nonce: 0 });
  const pulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Glow-then-advance: delays the built card so the chips light up first. */
  const glowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerPulse = useCallback((tokens: string[]) => {
    if (tokens.length === 0) return;
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    setPulse((p) => ({ tokens, nonce: p.nonce + 1 }));
    pulseTimerRef.current = setTimeout(
      () => setPulse((p) => ({ tokens: [], nonce: p.nonce })),
      1400,
    );
  }, []);

  // Stable reads for async callbacks.
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const voiceLoadRef = useRef(voiceLoad);
  voiceLoadRef.current = voiceLoad;
  /** One local "today" for the whole capture session (dates resolve against it). */
  const todayRef = useRef(new Date());

  // ── Recorder (same stack as LRDirectVoicePostCard) ─────────────────────
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const cancelledRef = useRef(false);
  const autoStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearRecordingTimers = useCallback(() => {
    if (autoStopTimerRef.current) clearTimeout(autoStopTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    autoStopTimerRef.current = null;
    countdownRef.current = null;
  }, []);

  const processAudio = useCallback(async (blob: Blob, purpose: RecordPurpose) => {
    setPhase("processing");
    try {
      const form = new FormData();
      form.append("audio", blob, blob.type.includes("mp4") ? "voice.m4a" : "voice.webm");
      form.append("mode", purpose === "build" ? "builder" : "builder_correct");
      form.append("client_date", getLocalTodayYMD());
      form.append("client_tz", Intl.DateTimeFormat().resolvedOptions().timeZone || "");
      if (purpose === "correct") {
        form.append("current_load", JSON.stringify(voiceLoadRef.current));
      }

      const { data, error } = await supabase.functions.invoke("voice-post", { body: form });
      if (error) throw new Error(error.message || "Voice processing failed");
      if ((data as any)?.error) throw new Error((data as any).error);

      const said = String((data as any)?.transcript ?? "");
      setTranscript(said);

      if (purpose === "build") {
        const load = normalizeBuilderLoad((data as any)?.load);
        setVoiceLoad(load);
        setDismissed(new Set());
        setLastChange(null);
        setEditingKey(null);
        // Phase C: flash everything Atlas just filled + soft "got it" cue.
        const filled: string[] = [];
        for (const s of load.stops) {
          if (s.city.trim()) filled.push(`stop.${s.id}`);
          if (s.date_text) filled.push(`date.${s.id}`);
        }
        if (load.equipment) filled.push("equipment");
        if (load.weight_lbs) filled.push("weight");
        if (load.length_ft) filled.push("length");
        if (load.commodity) filled.push("commodity");
        if (load.temperature_f != null) filled.push("temperature_f");
        if (load.handling.length > 0) filled.push("handling");
        if (load.notes) filled.push("notes");
        triggerPulse(filled);

        if (filled.length > 0) {
          voiceCues.success();
          // Glow-then-advance: hold the record screen one beat so the shipper
          // SEES the chips light up, then advance to the built-load card.
          setPhase("glow");
          if (glowTimerRef.current) clearTimeout(glowTimerRef.current);
          glowTimerRef.current = setTimeout(() => setPhase("built"), GLOW_ADVANCE_MS);
        } else {
          setPhase("built");
        }
        if (load.stops.length === 0 && !load.equipment) {
          toast({
            variant: "destructive",
            title: "Didn't catch much",
            description: "Try again — lead with the route, dates, equipment and weight.",
          });
        }
        return;
      }

      // Correction: ops-only → deterministic reducer → only touched fields move.
      const ops = (Array.isArray((data as any)?.ops) ? (data as any).ops : []) as VoiceOp[];
      const serverRejected = (Array.isArray((data as any)?.rejected) ? (data as any).rejected : []) as {
        reason?: string;
      }[];
      const current = voiceLoadRef.current;
      if (!current) throw new Error("No load to correct — record your load first.");
      const result = applyVoiceOps(current, ops, { today: todayRef.current });
      setVoiceLoad(result.load);

      // Fields the correction touched drop their dismissed confirmations so any
      // NEW uncertainty on them surfaces again.
      const touched = new Set<string>();
      for (const op of result.applied) {
        if (op.op === "update_stop" || op.op === "set_pickup_date" || op.op === "set_delivery_date") {
          touched.add(op.stop_id ? `stop.${op.stop_id}` : "stop.");
        } else if (op.op === "add_stop" || op.op === "remove_stop") {
          touched.add("stop.");
        } else if (op.op === "set_handling") {
          touched.add("handling");
        } else {
          touched.add(op.op.replace(/^set_/, ""));
        }
      }
      setDismissed((prev) => {
        const next = new Set<string>();
        for (const k of prev) {
          if (![...touched].some((t) => k.startsWith(t))) next.add(k);
        }
        return next;
      });

      // Phase C: flash the fields this correction touched (route + date rows
      // share a stop id, so `stop.<id>` also lights `date.<id>`).
      const pulseTokens = new Set<string>();
      for (const t of touched) {
        pulseTokens.add(t);
        if (t.startsWith("stop.")) pulseTokens.add(t.replace(/^stop\./, "date."));
      }
      triggerPulse([...pulseTokens]);
      if (result.applied.length > 0) voiceCues.success();

      const notes = [
        ...result.warnings,
        ...result.rejected.map((r) => r.reason),
        ...serverRejected.map((r) => String(r.reason ?? "Rejected")).filter(Boolean),
      ];
      setLastChange({ count: result.applied.length, notes });
      setPhase("built");
    } catch (e) {
      console.error("[WizardVoiceCapture] process failed", e);
      setErrorMsg(e instanceof Error ? e.message : "Could not process the recording");
      // Corrections land back on the card; a failed first parse goes to error.
      setPhase(voiceLoadRef.current ? "built" : "error");
      toast({
        variant: "destructive",
        title: "Voice hiccup",
        description: e instanceof Error ? e.message : "Could not process the recording",
      });
    }
  }, [toast, triggerPulse]);

  const startRecording = useCallback(async (purpose: RecordPurpose) => {
    try {
      setRecordPurpose(purpose);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/mp4";

      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      cancelledRef.current = false;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        clearRecordingTimers();
        setLiveStream(null);
        stream.getTracks().forEach((t) => t.stop());
        if (cancelledRef.current) {
          cancelledRef.current = false;
          chunksRef.current = [];
          setPhase(voiceLoadRef.current ? "built" : "idle");
          return;
        }
        await processAudio(new Blob(chunksRef.current, { type: mimeType }), purpose);
      };

      recorder.start();
      recorderRef.current = recorder;
      setLiveStream(stream);
      voiceCues.recordStart();
      setErrorMsg("");
      setPhase("recording");

      clearRecordingTimers();
      setSecondsLeft(MAX_RECORDING_SECONDS);
      countdownRef.current = setInterval(() => {
        setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
      }, 1000);
      autoStopTimerRef.current = setTimeout(() => {
        if (recorderRef.current?.state === "recording") {
          voiceCues.recordStop();
          recorderRef.current.stop();
        }
      }, MAX_RECORDING_SECONDS * 1000);
    } catch (e) {
      console.error("Mic access error:", e);
      toast({
        variant: "destructive",
        title: "Microphone access denied",
        description: "Allow microphone access to post by voice.",
      });
    }
  }, [processAudio, clearRecordingTimers, toast]);

  const stopRecording = useCallback(() => {
    clearRecordingTimers();
    if (recorderRef.current?.state === "recording") {
      voiceCues.recordStop();
      recorderRef.current.stop();
    }
  }, [clearRecordingTimers]);

  // Release the mic on unmount.
  useEffect(() => {
    return () => {
      clearRecordingTimers();
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
      if (glowTimerRef.current) clearTimeout(glowTimerRef.current);
      setLiveStream(null);
      const rec = recorderRef.current;
      if (rec && rec.state !== "inactive") {
        cancelledRef.current = true;
        try {
          rec.stop();
        } catch {
          /* noop */
        }
      }
      recorderRef.current?.stream?.getTracks().forEach((t) => t.stop());
    };
  }, [clearRecordingTimers]);

  // ── voiceLoad → WizardFields (the wizard page owns fields; we patch it) ──
  useEffect(() => {
    if (!voiceLoad) return;
    let cancelled = false;
    mapVoiceLoadToWizard(voiceLoad, {
      today: todayRef.current,
      current: fieldsRef.current,
      geocode: (city, state) => resolveSpokenCity(city, state),
    })
      .then(({ patch, stopDates: sd }) => {
        if (cancelled) return;
        setStopDates(sd);
        onChangeRef.current(patch);
      })
      .catch((e) => console.warn("[WizardVoiceCapture] mapping failed", e));
    return () => {
      cancelled = true;
    };
  }, [voiceLoad]);

  // ── Validation + gating ─────────────────────────────────────────────────
  const validation = useMemo(
    () => (voiceLoad ? validateVoiceLoad(voiceLoad, todayRef.current) : null),
    [voiceLoad],
  );

  const confKey = (c: VoiceLoadConfirmation) => `${c.field}::${c.note}`;
  const pending = useMemo(
    () => (validation?.confirmations ?? []).filter((c) => !dismissed.has(confKey(c))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validation, dismissed],
  );

  const blockers = useMemo(() => {
    if (!voiceLoad || !validation) return ["Tell Atlas about your load to continue."];
    const b: string[] = [...validation.issues];
    for (const c of pending) b.push(c.note);
    // Delivery date is required on bid loads (the form's rule) — a quote can skip it.
    if (postType === "load" && !fields.deliveryDate) {
      b.push("Delivery date is required on bid loads — say it or tap a date below.");
    }
    return b;
  }, [voiceLoad, validation, pending, postType, fields.deliveryDate]);

  const onGateChangeRef = useRef(onGateChange);
  onGateChangeRef.current = onGateChange;
  useEffect(() => {
    onGateChangeRef.current(blockers);
  }, [blockers]);

  // ── Tap-edit path: the SAME reducer as voice corrections ───────────────
  const applyOps = useCallback((ops: VoiceOp[], dismissPrefixes: string[]) => {
    const current = voiceLoadRef.current;
    if (!current) return;
    const result = applyVoiceOps(current, ops, { today: todayRef.current });
    setVoiceLoad(result.load);
    if (result.rejected.length > 0) {
      console.warn("[WizardVoiceCapture] tap edit rejected", result.rejected);
    }
    setDismissed((prev) => {
      const next = new Set(prev);
      for (const k of [...next]) {
        if (dismissPrefixes.some((p) => k.startsWith(p))) next.delete(k);
      }
      return next;
    });
  }, []);

  const dismissConfs = useCallback((confs: VoiceLoadConfirmation[]) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      for (const c of confs) next.add(confKey(c));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Amber candidate chip → concrete ops (where the field allows it). */
  const applyCandidate = (c: VoiceLoadConfirmation, cand: string) => {
    const current = voiceLoadRef.current;
    if (!current) return;
    const ops: VoiceOp[] = [];
    const stopMatch = /^stop\.([^.]+)\.(\w+)$/.exec(c.field);
    const cityState = /^(.+?),\s*([A-Za-z]{2})$/.exec(cand.trim());
    if (stopMatch) {
      const [, stopId, part] = stopMatch;
      const stop = current.stops.find((s) => s.id === stopId);
      if (part === "date" && stop) {
        ops.push({
          op: stop.stop_type === "drop" ? "set_delivery_date" : "set_pickup_date",
          stop_id: stopId,
          date_text: cand,
        });
      } else if (cityState) {
        ops.push({ op: "update_stop", stop_id: stopId, city: cityState[1], state: cityState[2] });
      } else if (/^[A-Za-z]{2}$/.test(cand.trim())) {
        ops.push({ op: "update_stop", stop_id: stopId, state: cand.trim() });
      } else {
        ops.push({ op: "update_stop", stop_id: stopId, city: cand.trim() });
      }
    } else if (/^temperature/.test(c.field)) {
      ops.push({ op: "set_temperature", value: cand });
    } else if (/^weight/.test(c.field)) {
      ops.push({ op: "set_weight", value: cand });
    } else if (/^length/.test(c.field)) {
      ops.push({ op: "set_length", value: cand });
    } else if (/^equipment/.test(c.field)) {
      ops.push({ op: "set_equipment", value: cand });
    } else if (c.stopId || c.field.startsWith("stop")) {

      // Generic stop ambiguity ("Fort Worth or Dallas, whichever works").
      const target =
        current.stops.find((s) => s.id === c.stopId) ??
        current.stops.find(
          (s) => s.city && c.note.toLowerCase().includes(s.city.toLowerCase()),
        );
      if (!target) return; // not actionable as a chip — the note stays
      ops.push(
        cityState
          ? { op: "update_stop", stop_id: target.id, city: cityState[1], state: cityState[2] }
          : { op: "update_stop", stop_id: target.id, city: cand.trim() },
      );
    } else {
      return;
    }
    applyOps(ops, [c.field]);
  };

  /** Whether a candidate chip can be turned into an op. */
  const candidateActionable = (c: VoiceLoadConfirmation): boolean => {
    if (/^stop\.[^.]+\.(state|date)$/.test(c.field)) return true;
    if (/^(temperature|weight|equipment)/.test(c.field)) return true;
    const current = voiceLoadRef.current;
    if (!current) return false;
    return (
      !!current.stops.find((s) => s.id === c.stopId) ||
      current.stops.some((s) => s.city && c.note.toLowerCase().includes(s.city.toLowerCase()))
    );
  };

  // ── Field-state helpers ─────────────────────────────────────────────────
  const confsFor = (pred: (c: VoiceLoadConfirmation) => boolean) => pending.filter(pred);

  const stateFor = (
    missing: boolean,
    confs: VoiceLoadConfirmation[],
    extraConfirm = false,
  ): VoiceFieldState => (missing ? "missing" : confs.length > 0 || extraConfirm ? "confirm" : "captured");

  // Candidates live on the load's ambiguities; confirmations reference them by field.
  const candidatesFor = (c: VoiceLoadConfirmation): string[] =>
    voiceLoad?.ambiguities.find((a) => a.field === c.field)?.candidates ?? [];

  const shellMeta = (confs: VoiceLoadConfirmation[]) => {
    const heard = confs.map((c) => {
      const m = /Heard "([^"]+)"/.exec(c.note);
      return m?.[1] ?? null;
    }).find(Boolean) ?? null;
    return {
      heard,
      note: confs.map((c) => c.note).join(" ") || null,
      candidates: confs.flatMap((c) => candidatesFor(c)).slice(0, 4),
      firstConf: confs[0],
    };
  };

  // ── RENDER ──────────────────────────────────────────────────────────────
  const recording = phase === "recording";
  const processing = phase === "processing";
  /** Brief post-parse beat: chips glow on the record screen before advancing. */
  const glowing = phase === "glow";
  const micLevel = useMicVolumeMeter(liveStream, recording);

  /** True while a shell's fill-flash runs. Tokens match shell keys
   *  (`stop.<id>`, `date.<id>`, `equipment`, …); prefix match so the
   *  correction token `temperature` also lights `temperature_f`. */
  const isPulsing = (key: string) => pulse.tokens.some((t) => key === t || key.startsWith(t));

  // ── Guided voice script — sequential chips in posting order ──────────────
  // The chips are a SPEAKING SEQUENCE (the order Atlas wants to hear things),
  // not a static checklist. They fill live off the same 3-state logic the
  // load-being-built card uses, so they glow the instant Atlas captures a
  // field. Weight + Length are optional (default 41,000 lbs / 53 ft) and live
  // in the optional row — they never block posting.
  const guidedChips = useMemo(() => {
    const pickups = (voiceLoad?.stops ?? []).filter((s) => s.stop_type === "pickup");
    const drops = (voiceLoad?.stops ?? []).filter((s) => s.stop_type === "drop");
    const firstPickup = pickups[0];
    const lastDelivery = drops[drops.length - 1];

    const routeConfs = (stopId: string) =>
      confsFor((c) => c.field.startsWith(`stop.${stopId}.`) && !c.field.endsWith(".date"));
    const dateConfs = (stopId: string) =>
      confsFor(
        (c) =>
          c.field === `stop.${stopId}.date` || (c.stopId === stopId && c.field.endsWith(".date")),
      );
    const equipmentConfs = confsFor((c) => /^equipment/.test(c.field));
    const commodityConfs = confsFor((c) => /^commodity/.test(c.field));

    const countState = (n: number): VoiceFieldState => (n > 0 ? "captured" : "missing");
    const citiesState = (list: typeof pickups): VoiceFieldState => {
      if (list.length === 0) return "missing";
      const missing = list.some((s) => !s.city.trim());
      const confs = list.flatMap((s) => routeConfs(s.id));
      return stateFor(missing, confs);
    };
    const dateState = (stop: typeof firstPickup): VoiceFieldState => {
      if (!stop) return "missing";
      const sd = stopDates[stop.id];
      return stateFor(!sd?.date, dateConfs(stop.id), sd?.needsConfirmation === true);
    };

    // Posting order: counts → cities → dates → equipment → commodity.
    const items: { key: string; label: string; hint: string; state: VoiceFieldState }[] = [
      { key: "num-picks", label: "# Pickups", hint: "two", state: countState(pickups.length) },
      { key: "num-drops", label: "# Drops", hint: "one", state: countState(drops.length) },
      { key: "pickup-cities", label: "Pickup cities", hint: "Miami, Florida", state: citiesState(pickups) },
      { key: "drop-cities", label: "Drop cities", hint: "Bronx, New York", state: citiesState(drops) },
      { key: "pickup-date", label: "Pickup date", hint: "today or August 25th", state: dateState(firstPickup) },
    ];
    if (postType === "load") {
      items.push({ key: "delivery-date", label: "Delivery date", hint: "Friday or August 27th", state: dateState(lastDelivery) });
    }
    items.push({
      key: "equipment",
      label: "Equipment",
      hint: "van, reefer, flatbed",
      state: stateFor(!voiceLoad?.equipment, equipmentConfs),
    });
    items.push({
      key: "commodity",
      label: "Commodity",
      hint: "produce, dry goods",
      state: stateFor(!(voiceLoad?.commodity ?? "").trim(), commodityConfs),
    });
    return items;
  }, [voiceLoad, postType, stopDates, pending, dismissed]);

  // Optional-but-encouraged chips (weight + length live here — defaults apply).
  // Weight/Length are NEVER "missing": unstated → captured with the default
  // (41,000 lbs / 53'), stated → captured with the spoken value.
  const optionalChips = useMemo(() => {
    const current = voiceLoad;
    const notes = (current?.notes ?? "").toLowerCase();
    const handling = (current?.handling ?? []).join(" ").toLowerCase();
    const combined = `${notes} ${handling}`;
    const weightSpoken = current?.weight_lbs != null && current.weight_lbs > 0;
    const lengthSpoken = current?.length_ft != null && current.length_ft > 0;
    const items: { key: string; label: string; state: VoiceFieldState }[] = [];
    items.push({
      key: "weight",
      label: weightSpoken
        ? `Weight · ${current!.weight_lbs!.toLocaleString()} lbs`
        : `Weight · ${DEFAULT_WEIGHT_LBS.toLocaleString()} lbs (default)`,
      state: "captured",
    });
    items.push({
      key: "length",
      label: lengthSpoken ? `Length · ${current!.length_ft}'` : `Length · ${DEFAULT_LENGTH_FT}' (default)`,
      state: "captured",
    });
    items.push({
      key: "temperature",
      label: "Temperature",
      state: current?.temperature_f != null ? "captured" : "missing",
    });
    items.push({
      key: "team",
      label: "Team drivers",
      state: /\bteam\b/.test(combined) ? "captured" : "missing",
    });
    items.push({
      key: "hazmat",
      label: "Hazmat",
      state: /\bhazmat\b/.test(combined) ? "captured" : "missing",
    });
    items.push({
      key: "appointment",
      label: "Appointment times",
      state: /\b(appointment|fcfs|first come)\b/.test(combined) ? "captured" : "missing",
    });
    items.push({
      key: "dock",
      label: "Dock hours",
      state: /\b(dock|hours)\b/.test(combined) ? "captured" : "missing",
    });
    items.push({
      key: "special",
      label: "Special instructions",
      state: (current?.notes ?? "").trim() ? "captured" : "missing",
    });
    return items;
  }, [voiceLoad]);

  const guidedVoiceScript = (
    <div className="space-y-2">
      <p className="inline-flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-white/80">
        <ListChecks className="h-3.5 w-3.5" /> Say it in this order
      </p>
      <div className="flex flex-wrap items-start justify-center gap-x-0.5 gap-y-1.5 sm:gap-x-1 sm:gap-y-2.5">
        {guidedChips.map((item, i) => {
          const icon =
            item.state === "captured" ? (
              <Check className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            ) : item.state === "confirm" ? (
              <AlertTriangle className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            ) : (
              <Circle className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            );
          return (
            <span key={item.key} className="inline-flex items-start gap-0.5 sm:gap-1">
              <span className="inline-flex flex-col items-center gap-0.5 sm:gap-1">
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold transition-colors sm:px-2.5 sm:py-1 sm:text-[11px]",
                    item.state === "captured" && "voice-check-captured",
                    item.state === "confirm" && "voice-check-confirm",
                    item.state === "missing" && "voice-check-missing",
                  )}
                >
                  {icon}
                  {item.label}
                </span>
                <span className="whitespace-nowrap text-[8px] font-normal leading-none text-white/65 sm:text-[9px]">
                  “{item.hint}”
                </span>
              </span>
              {i < guidedChips.length - 1 && (
                <span aria-hidden className="hidden sm:block mt-1.5 select-none text-[10px] text-white/40">
                  →
                </span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );




  const micButton = (size: "lg" | "sm") => (
    <span className="relative inline-flex">
      {recording && (
        <>
          <span className="voice-mic-ring" />
          <span className="voice-mic-ring" style={{ animationDelay: "0.8s" }} />
        </>
      )}
      <button
        type="button"
        onClick={() => (recording ? stopRecording() : startRecording(size === "lg" ? "build" : "correct"))}
        disabled={processing || glowing}
        aria-label={recording ? "Stop recording" : "Start recording"}
        className={cn(
          "inline-flex items-center justify-center rounded-full text-white transition-all",
          "disabled:opacity-50",
          size === "lg" ? "h-20 w-20" : "h-11 w-11 flex-shrink-0",
          recording ? "bg-red-500" : "bg-[hsl(var(--brand-teal))] hover:scale-105 active:scale-95",
        )}
        style={{
          boxShadow: recording
            ? "0 4px 14px hsl(4 80% 58% / 0.4)"
            : "0 4px 14px hsl(var(--brand-teal) / 0.35)",
        }}
      >
        {processing ? (
          <Loader2 className={cn("animate-spin", size === "lg" ? "h-8 w-8" : "h-5 w-5")} />
        ) : recording ? (
          <Square className={size === "lg" ? "h-7 w-7" : "h-4 w-4"} />
        ) : (
          <Mic className={size === "lg" ? "h-8 w-8" : "h-5 w-5"} />
        )}
      </button>
    </span>
  );

  // CAPTURE HERO (idle / recording / processing / error)
  if (phase !== "built" || !voiceLoad) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div
          className="rounded-2xl border p-6 sm:p-8 text-center space-y-4"
          style={{
            background: "linear-gradient(135deg, #0B8A80 0%, #046D66 55%, #02514D 100%)",
            borderColor: "hsl(var(--brand-teal) / 0.4)",
          }}
        >
          <div className="space-y-1.5">
            <h3 className="text-lg font-bold text-white">Tell Atlas about your load</h3>
            <p className="text-xs text-white/85 max-w-md mx-auto leading-relaxed">
              Say it in this order and watch Atlas fill it in as you talk.
            </p>
          </div>

          <div className="flex flex-col items-center gap-2.5 py-2">
            {micButton("lg")}
            {recording && <VoiceWaveform level={micLevel} className="h-5 text-white/90" />}
            <p className="inline-flex items-center text-[11px] font-medium text-white/80">
              {recording ? (
                `Listening… tap to stop (auto-stops at ${MAX_RECORDING_SECONDS}s — ${secondsLeft}s left)`
              ) : processing ? (
                <>
                  Atlas is organizing your load
                  <span className="inline-flex items-end gap-0.5 pl-1" aria-hidden>
                    <span className="voice-dot inline-block h-1 w-1 rounded-full bg-white/80" />
                    <span className="voice-dot inline-block h-1 w-1 rounded-full bg-white/80" style={{ animationDelay: "0.15s" }} />
                    <span className="voice-dot inline-block h-1 w-1 rounded-full bg-white/80" style={{ animationDelay: "0.3s" }} />
                  </span>
                </>
              ) : glowing ? (
                "Got it — building your load…"
              ) : (
                "Tap the mic and start talking"
              )}
            </p>
          </div>

          {/* Guided voice script — sequential chips in posting order */}
          {guidedVoiceScript}

          {/* Optional but encouraged — secondary chip row (Weight + Length live here) */}
          <div className="space-y-1.5">
            <p className="inline-flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-white/60">
              Optional but encouraged
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-1.5">
              {optionalChips.map((item, i) => {
                const icon =
                  item.state === "captured" ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <Circle className="h-3 w-3" />
                  );
                return (
                  <span key={item.key} className="inline-flex items-center gap-1">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold transition-colors",
                        item.state === "captured" && "voice-check-captured",
                        item.state === "missing" && "voice-check-missing",
                      )}
                    >
                      {icon}
                      {item.label}
                    </span>
                    {i < optionalChips.length - 1 && (
                      <span aria-hidden className="text-[9px] text-white/30 select-none">
                        →
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
          </div>

          {/* "Say it like this" template + examples */}
          <div className="space-y-2 rounded-xl border border-white/10 bg-white/5 p-3 text-left backdrop-blur-sm">
            <p className="text-[11px] leading-relaxed text-white/85">
              <span className="font-semibold text-white">Say it like this:</span>{" "}
              “[number] picks and [number] drops, loading in [pickup city, state], dropping in
              [drop city, state], pickup [date], delivery [date], [equipment], commodity is
              [commodity].”
            </p>
            <div className="space-y-1.5 border-t border-white/10 pt-2">
              <p className="text-[11px] leading-relaxed text-white/80">
                • “2 picks and 1 drop, loading in Salinas, California and Miami, Florida, dropping
                in Bronx, New York, pickup tomorrow, delivery Friday, reefer, 41,000 pounds,
                produce.”
              </p>
              <p className="text-[11px] leading-relaxed text-white/80">
                • “1 pick and 2 drops, loading in Dallas, Texas, dropping in Newark, New Jersey and
                Boston, Massachusetts, pickup today, delivery Thursday, van, 38,000 pounds, dry
                goods.”
              </p>
            </div>
          </div>

          {/* Defaults note */}
          <p className="text-center text-[10px] leading-relaxed text-white/50">
            * Weight defaults to 41,000 lbs and trailer length to 53' unless you say otherwise.
          </p>


          {phase === "error" && errorMsg && (
            <div className="rounded-lg bg-white/10 px-3 py-2 text-[11px] text-white">
              {errorMsg} — tap the mic to try again.
            </div>
          )}
        </div>


        <p className="text-center text-[11px] text-muted-foreground">
          Prefer to type it?{" "}
          <button
            type="button"
            onClick={onSwitchToType}
            className="font-semibold underline underline-offset-2"
            style={{ color: "hsl(var(--brand-teal-deep))" }}
          >
            Switch to Type mode
          </button>{" "}
          — everything captured so far carries over.
        </p>
      </div>
    );
  }

  // LOAD-BEING-BUILT CARD
  const isReefer =
    isReeferEquipment(voiceLoad.equipment) ||
    voiceEquipmentToEnum(voiceLoad.equipment) === "reefer";
  const weightNum = voiceLoad.weight_lbs;
  const lengthNum = voiceLoad.length_ft;


  // Stop rows come from fields.stops (mapped from voiceLoad — same ids, order).
  let pickupIdx = 0;
  let dropIdx = 0;
  const stopRows = fields.stops.map((ws) => {
    const vs = voiceLoad.stops.find((s) => s.id === ws.id);
    if (!vs) return null;
    const isPickup = vs.stop_type === "pickup";
    const label = isPickup
      ? pickupLabel(pickupIdx++, fields.pickupCount)
      : dropLabel(dropIdx++, fields.dropCount);
    return { ws, vs, isPickup, label };
  }).filter((r): r is NonNullable<typeof r> => !!r);

  // Confirmations grouped: per-stop state/route vs per-stop date vs general.
  const routeConfs = (stopId: string) =>
    confsFor((c) => c.field.startsWith(`stop.${stopId}.`) && !c.field.endsWith(".date"));
  const dateConfs = (stopId: string) =>
    confsFor((c) =>
      c.field === `stop.${stopId}.date` || (c.stopId === stopId && c.field.endsWith(".date")),
    );
  const handledFields = new Set<string>();
  for (const r of stopRows) {
    routeConfs(r.vs.id).forEach((c) => handledFields.add(confKey(c)));
    dateConfs(r.vs.id).forEach((c) => handledFields.add(confKey(c)));
  }
  const fieldConf = (re: RegExp) => confsFor((c) => re.test(c.field) && !handledFields.has(confKey(c)));
  const equipmentConfs = fieldConf(/^equipment/);
  const weightConfs = fieldConf(/^weight/);
  const lengthConfs = fieldConf(/^length/);
  const commodityConfs = fieldConf(/^commodity/);
  const tempConfs = fieldConf(/^temperature/);
  const notesConfs = fieldConf(/^notes?/);
  const handlingConfs = fieldConf(/^handling/);
  const claimed = new Set<string>();
  [equipmentConfs, weightConfs, lengthConfs, commodityConfs, tempConfs, notesConfs, handlingConfs].forEach((g) =>
    g.forEach((c) => claimed.add(confKey(c))),
  );

  // Generic leftovers (e.g. "Fort Worth or Dallas, whichever works").
  const generalConfs = pending.filter((c) => !handledFields.has(confKey(c)) && !claimed.has(confKey(c)));

  const equipmentEnum = fields.equipmentType || voiceEquipmentToEnum(voiceLoad.equipment);

  // Raw spoken handling values paired with their chip labels (for remove ops).
  const handlingPairs = voiceLoad.handling.map((h) => ({
    raw: h,
    label: mapVoiceHandling([h])[0] ?? h,
  }));
  const chipOptions = Array.from(
    new Set([...HANDLING_OPTIONS, ...handlingPairs.map((p) => p.label)]),
  );


  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-bold text-foreground">Your load, as Atlas heard it</h3>
          <button
            type="button"
            onClick={() => setShowTranscript((s) => !s)}
            className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
          >
            What Atlas heard
            <ChevronDown className={cn("h-3 w-3 transition-transform", showTranscript && "rotate-180")} />
          </button>
          {showTranscript && transcript && (
            <div
              className="mt-2 max-w-md animate-fade-in rounded-lg border-l-2 bg-muted/50 px-3 py-2"
              style={{ borderColor: "hsl(var(--brand-teal) / 0.5)" }}
            >
              <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
                Transcript
              </p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
                “{transcript}”
              </p>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => startRecording("build")}
          disabled={recording || processing}
          className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
        >
          <RotateCcw className="h-3 w-3" />
          Re-record
        </button>
      </div>

      {/* Voice-correction feedback */}
      {lastChange && (
        <div
          className="animate-fade-in rounded-xl border px-3 py-2.5 space-y-1"
          style={{
            borderColor: "hsl(var(--brand-teal) / 0.35)",
            backgroundColor: "hsl(var(--brand-teal) / 0.06)",
          }}
        >
          <p className="text-xs font-semibold" style={{ color: "hsl(var(--brand-teal-deep))" }}>
            {lastChange.count > 0
              ? `Atlas changed ${lastChange.count} thing${lastChange.count === 1 ? "" : "s"}`
              : "No changes heard"}
          </p>
          {lastChange.notes.map((n, i) => (
            <p key={i} className="text-[11px] text-muted-foreground">
              {n}
            </p>
          ))}
        </div>
      )}

      {/* ROUTE */}
      <section className="space-y-2 animate-fade-in" style={{ animationDelay: "60ms", animationFillMode: "backwards" }}>
        <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
          <MapPin className="h-3.5 w-3.5" /> Route
        </p>
        <div className="space-y-2">
          {stopRows.map(({ ws, vs, label }) => {
            const confs = routeConfs(vs.id);
            const meta = shellMeta(confs);
            const missing = !vs.city.trim();
            const editKey = `stop.${vs.id}`;
            return (
              <VoiceFieldShell
                key={vs.id}
                label={label}
                state={stateFor(missing, confs)}
                pulse={isPulsing(`stop.${vs.id}`)}
                pulseNonce={pulse.nonce}
                heard={meta.heard}
                note={meta.note}
                candidates={meta.firstConf && candidateActionable(meta.firstConf) ? meta.candidates : []}
                onCandidate={(cand) => meta.firstConf && applyCandidate(meta.firstConf, cand)}
                onConfirm={confs.length ? () => dismissConfs(confs) : undefined}
                editing={editingKey === editKey}
                onEditToggle={() => setEditingKey((k) => (k === editKey ? null : editKey))}
                display={
                  <p className={cn("text-sm font-medium", missing ? "text-muted-foreground" : "text-foreground")}>
                    {missing ? "Not heard yet — tap Edit to set it" : ws.location || `${vs.city}${vs.state ? `, ${vs.state}` : ""}`}
                  </p>
                }
                editor={
                  <StopEditor
                    initial={ws.location || `${vs.city}${vs.state ? `, ${vs.state}` : ""}`}
                    onPick={(city, state) => {
                      applyOps([{ op: "update_stop", stop_id: vs.id, city, state }], [`stop.${vs.id}`]);
                      setEditingKey(null);
                    }}
                  />
                }
              />
            );
          })}
        </div>
      </section>

      {/* DATES */}
      <section className="space-y-2 animate-fade-in" style={{ animationDelay: "120ms", animationFillMode: "backwards" }}>
        <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
          <CalendarDays className="h-3.5 w-3.5" /> Dates
        </p>
        <div className="space-y-2">
          {stopRows.map(({ ws, vs, label }) => {
            const confs = dateConfs(vs.id);
            const meta = shellMeta(confs);
            const sd = stopDates[vs.id];
            const needsConfirm = confs.length > 0 || sd?.needsConfirmation === true;
            const displayDate = sd?.date ?? ws.scheduled_date;
            const editKey = `date.${vs.id}`;
            return (
              <VoiceFieldShell
                key={vs.id}
                label={`${label} date${vs.city ? ` — ${vs.city}` : ""}`}
                state={stateFor(false, confs, needsConfirm)}
                pulse={isPulsing(`date.${vs.id}`)}
                pulseNonce={pulse.nonce}
                heard={meta.heard ?? (sd?.source === "spoken" ? vs.date_text : null)}
                note={meta.note ?? sd?.note ?? null}
                candidates={meta.firstConf && candidateActionable(meta.firstConf) ? meta.candidates : []}
                onCandidate={(cand) => meta.firstConf && applyCandidate(meta.firstConf, cand)}
                onConfirm={
                  needsConfirm
                    ? () => {
                        dismissConfs(confs);
                        // Resolver-ambiguous dates (no validation confirmation)
                        // clear by rewriting the date_text to the shown date.
                        if (confs.length === 0 && sd?.date) {
                          applyOps(
                            [
                              {
                                op: vs.stop_type === "drop" ? "set_delivery_date" : "set_pickup_date",
                                stop_id: vs.id,
                                date_text: phraseFromYMD(sd.date),
                              },
                            ],
                            [`stop.${vs.id}.date`],
                          );
                        }
                      }
                    : undefined
                }
                editing={editingKey === editKey}
                onEditToggle={() => setEditingKey((k) => (k === editKey ? null : editKey))}
                display={
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground">
                      {displayDate
                        ? formatYYYYMMDDToLocaleDateString(displayDate, "en-US", { month: "short", day: "numeric", year: "numeric" })
                        : "Not heard yet"}
                    </p>
                    {sd?.source === "default" && (
                      <span className="rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Default
                      </span>
                    )}
                  </div>
                }
                editor={
                  <DatePickerField
                    value={displayDate || null}
                    onChange={(v) => {
                      if (!v) return;
                      applyOps(
                        [
                          {
                            op: vs.stop_type === "drop" ? "set_delivery_date" : "set_pickup_date",
                            stop_id: vs.id,
                            date_text: phraseFromYMD(v),
                          },
                        ],
                        [`stop.${vs.id}.date`],
                      );
                      setEditingKey(null);
                    }}
                    placeholder={vs.stop_type === "pickup" ? "Pickup date" : "Delivery date"}
                    displayFormat="MMM d, yyyy"
                    className="h-9 text-xs"
                  />
                }
              />
            );
          })}
        </div>
      </section>

      {/* LOAD */}
      <section className="space-y-2 animate-fade-in" style={{ animationDelay: "180ms", animationFillMode: "backwards" }}>
        <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
          <Truck className="h-3.5 w-3.5" /> Load
        </p>
        <div className="space-y-2">
          {/* Equipment */}
          {(() => {
            const meta = shellMeta(equipmentConfs);
            const editKey = "equipment";
            return (
              <VoiceFieldShell
                label="Equipment"
                state={stateFor(!voiceLoad.equipment, equipmentConfs)}
                pulse={isPulsing("equipment")}
                pulseNonce={pulse.nonce}
                heard={meta.heard}
                note={meta.note}
                candidates={meta.candidates}
                onCandidate={(cand) => meta.firstConf && applyCandidate(meta.firstConf, cand)}
                onConfirm={equipmentConfs.length ? () => dismissConfs(equipmentConfs) : undefined}
                editing={editingKey === editKey}
                onEditToggle={() => setEditingKey((k) => (k === editKey ? null : editKey))}
                display={
                  <p className={cn("text-sm font-medium", voiceLoad.equipment ? "text-foreground" : "text-muted-foreground")}>
                    {voiceLoad.equipment
                      ? equipmentLabel(equipmentEnum, voiceLoad.equipment)
                      : "Not heard yet — say it or pick one"}
                  </p>
                }
                editor={
                  <div className="space-y-1.5">
                    <EquipmentSelect
                      value={equipmentEnum}
                      onChange={(val) => {
                        applyOps([{ op: "set_equipment", value: String(val) }], ["equipment"]);
                        setEditingKey(null);
                      }}
                      mode="single"
                      placeholder="Equipment Type"
                      className="h-9 text-xs"
                    />
                    <ToggleChipRow
                      chips={EQUIPMENT_CHIPS}
                      currentValue={equipmentEnum}
                      onSelect={(v) => {
                        applyOps([{ op: "set_equipment", value: v }], ["equipment"]);
                        setEditingKey(null);
                      }}
                      onClear={() => undefined}
                    />
                  </div>
                }
              />
            );
          })()}

          {/* Weight — never "needed": unstated → the 41,000 lbs default applies */}
          {(() => {
            const meta = shellMeta(weightConfs);
            const editKey = "weight";
            const weightSpoken = !!weightNum && weightNum > 0;
            const weightShown = weightSpoken ? weightNum : DEFAULT_WEIGHT_LBS;
            return (
              <VoiceFieldShell
                label="Weight"
                state={stateFor(false, weightConfs)}
                pulse={isPulsing("weight")}
                pulseNonce={pulse.nonce}
                heard={meta.heard}
                note={meta.note}
                candidates={meta.candidates}
                onCandidate={(cand) => meta.firstConf && applyCandidate(meta.firstConf, cand)}
                onConfirm={weightConfs.length ? () => dismissConfs(weightConfs) : undefined}
                editing={editingKey === editKey}
                onEditToggle={() => setEditingKey((k) => (k === editKey ? null : editKey))}
                display={
                  <p className="text-sm font-medium text-foreground">
                    {weightShown.toLocaleString()} lbs
                    {!weightSpoken && (
                      <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        default
                      </span>
                    )}
                  </p>
                }
                editor={
                  <FieldInput
                    label="Weight (lbs)"
                    value={String(weightShown)}
                    onChange={(v) => {
                      if (v === "" || /^\d+$/.test(v)) {
                        if (v) applyOps([{ op: "set_weight", value: v }], ["weight"]);
                      }
                    }}
                    placeholder="e.g. 41500"
                  />
                }
              />
            );
          })()}

          {/* Length — never "needed": unstated → the 53' default applies */}
          {(() => {
            const meta = shellMeta(lengthConfs);
            const editKey = "length";
            const lengthSpoken = !!lengthNum && lengthNum > 0;
            const lengthShown = lengthSpoken ? lengthNum : DEFAULT_LENGTH_FT;
            return (
              <VoiceFieldShell
                label="Length"
                state={stateFor(false, lengthConfs)}
                pulse={isPulsing("length")}
                pulseNonce={pulse.nonce}
                heard={meta.heard}
                note={meta.note}
                candidates={meta.candidates}
                onCandidate={(cand) => meta.firstConf && applyCandidate(meta.firstConf, cand)}
                onConfirm={lengthConfs.length ? () => dismissConfs(lengthConfs) : undefined}
                editing={editingKey === editKey}
                onEditToggle={() => setEditingKey((k) => (k === editKey ? null : editKey))}
                display={
                  <p className="text-sm font-medium text-foreground">
                    {lengthShown}' trailer
                    {!lengthSpoken && (
                      <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        default
                      </span>
                    )}
                  </p>
                }
                editor={
                  <FieldInput
                    label="Length (ft)"
                    value={String(lengthShown)}
                    onChange={(v) => {
                      if (v === "" || /^\d+$/.test(v)) {
                        if (v) applyOps([{ op: "set_length", value: v }], ["length"]);
                      }
                    }}
                    placeholder="e.g. 53"
                  />
                }
              />
            );
          })()}

          {/* Commodity (optional) */}
          {(() => {

            const meta = shellMeta(commodityConfs);
            const editKey = "commodity";
            return (
              <VoiceFieldShell
                label="Commodity"
                state={stateFor(false, commodityConfs)}
                pulse={isPulsing("commodity")}
                pulseNonce={pulse.nonce}
                heard={meta.heard}
                note={meta.note}
                onConfirm={commodityConfs.length ? () => dismissConfs(commodityConfs) : undefined}
                editing={editingKey === editKey}
                onEditToggle={() => setEditingKey((k) => (k === editKey ? null : editKey))}
                display={
                  <p className={cn("text-sm font-medium", voiceLoad.commodity ? "text-foreground" : "text-muted-foreground")}>
                    {voiceLoad.commodity || "—"}
                  </p>
                }
                editor={
                  <FieldInput
                    label="Commodity"
                    value={voiceLoad.commodity ?? ""}
                    onChange={(v) => {
                      if (v.trim()) applyOps([{ op: "set_commodity", value: v }], ["commodity"]);
                    }}
                    placeholder="e.g. Produce"
                  />
                }
              />
            );
          })()}

          {/* Temperature — reefer only (nit guard: never shown on dry vans) */}
          {isReefer && (() => {
            const meta = shellMeta(tempConfs);
            const editKey = "temperature_f";
            const t = voiceLoad.temperature_f;
            return (
              <VoiceFieldShell
                label="Reefer temperature"
                state={stateFor(false, tempConfs, t === null)}
                pulse={isPulsing("temperature_f")}
                pulseNonce={pulse.nonce}
                heard={meta.heard}
                note={meta.note ?? (t === null ? "Reefer load with no temperature — confirm the set point." : null)}
                candidates={meta.candidates}
                onCandidate={(cand) => meta.firstConf && applyCandidate(meta.firstConf, cand)}
                onConfirm={tempConfs.length ? () => dismissConfs(tempConfs) : undefined}
                editing={editingKey === editKey}
                onEditToggle={() => setEditingKey((k) => (k === editKey ? null : editKey))}
                display={
                  <p className={cn("text-sm font-medium", t !== null ? "text-foreground" : "text-muted-foreground")}>
                    {t !== null ? `${t}°F` : "Not heard yet"}
                  </p>
                }
                editor={
                  <FieldInput
                    label="Temperature (°F)"
                    value={t !== null ? String(t) : ""}
                    onChange={(v) => {
                      if (v === "" || /^-?\d+$/.test(v)) {
                        if (v && v !== "-") applyOps([{ op: "set_temperature", value: v }], ["temperature_f"]);
                      }
                    }}
                    placeholder="e.g. 34"
                  />
                }
              />
            );
          })()}
        </div>
      </section>

      {/* NOTES */}
      <section className="space-y-2 animate-fade-in" style={{ animationDelay: "240ms", animationFillMode: "backwards" }}>
        <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
          <StickyNote className="h-3.5 w-3.5" /> Notes &amp; requirements
        </p>
        <div className="space-y-2">
          {/* Handling chips */}
          {(() => {
            const meta = shellMeta(handlingConfs);
            const editKey = "handling";
            return (
              <VoiceFieldShell
                label="Handling requirements"
                state={stateFor(false, handlingConfs)}
                pulse={isPulsing("handling")}
                pulseNonce={pulse.nonce}
                heard={meta.heard}
                note={meta.note}
                onConfirm={handlingConfs.length ? () => dismissConfs(handlingConfs) : undefined}
                editing={editingKey === editKey}
                onEditToggle={() => setEditingKey((k) => (k === editKey ? null : editKey))}
                display={
                  <div className="flex flex-wrap gap-1.5">
                    {fields.handlingRequirements.length === 0 ? (
                      <p className="text-sm text-muted-foreground">—</p>
                    ) : (
                      fields.handlingRequirements.map((h) => (
                        <span
                          key={h}
                          className="inline-flex items-center rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[10px] font-medium text-foreground"
                        >
                          {h}
                        </span>
                      ))
                    )}
                  </div>
                }
                editor={
                  <div className="flex flex-wrap gap-1.5">
                    {chipOptions.map((label) => {
                      const active = fields.handlingRequirements.includes(label);
                      return (
                        <button
                          key={label}
                          type="button"
                          aria-pressed={active}
                          onClick={() => {
                            const pair = handlingPairs.find(
                              (p) => p.label.toLowerCase() === label.toLowerCase(),
                            );
                            applyOps(
                              [
                                {
                                  op: "set_handling",
                                  value: active ? (pair?.raw ?? label) : label,
                                  action: active ? "remove" : "add",
                                },
                              ],
                              ["handling"],
                            );
                          }}
                          className={cn(
                            "inline-flex items-center rounded-full border px-2 py-0.5 text-[9px] font-medium transition-colors",
                            active
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border/40 bg-transparent text-muted-foreground hover:bg-muted/50",
                          )}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                }
              />
            );
          })()}

          {/* Comments */}
          {(() => {
            const meta = shellMeta(notesConfs);
            const editKey = "notes";
            return (
              <VoiceFieldShell
                label="Notes"
                state={stateFor(false, notesConfs)}
                pulse={isPulsing("notes")}
                pulseNonce={pulse.nonce}
                heard={meta.heard}
                note={meta.note}
                onConfirm={notesConfs.length ? () => dismissConfs(notesConfs) : undefined}
                editing={editingKey === editKey}
                onEditToggle={() => setEditingKey((k) => (k === editKey ? null : editKey))}
                display={
                  <p className={cn("text-sm", fields.comments ? "text-foreground" : "text-muted-foreground")}>
                    {fields.comments || "—"}
                  </p>
                }
                editor={
                  <Textarea
                    value={voiceLoad.notes ?? ""}
                    onChange={(e) => {
                      if (e.target.value.trim()) {
                        applyOps([{ op: "set_notes", value: e.target.value }], ["notes"]);
                      }
                    }}
                    placeholder="Anything brokers should know"
                    className="min-h-[56px] resize-none text-xs"
                  />
                }
              />
            );
          })()}
        </div>
      </section>

      {/* General ambiguities not tied to a rendered row */}
      {generalConfs.length > 0 && (
        <div
          className="rounded-xl border px-3 py-2.5 space-y-2"
          style={{
            borderColor: "hsl(38 92% 50% / 0.55)",
            backgroundColor: "hsl(38 92% 50% / 0.07)",
          }}
        >
          <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[hsl(28_95%_38%)]">
            <AlertTriangle className="h-3.5 w-3.5" /> Atlas isn't sure about
          </p>
          {generalConfs.map((c) => (
            <div key={confKey(c)} className="space-y-1">
              <p className="text-[11px] text-foreground">{c.note}</p>
              <div className="flex flex-wrap gap-1.5">
                {candidateActionable(c) &&
                  candidatesFor(c).map((cand) => (
                    <button
                      key={cand}
                      type="button"
                      onClick={() => applyCandidate(c, cand)}
                      className="rounded-full border border-[hsl(38_92%_50%/0.6)] bg-background px-2.5 py-1 text-[11px] font-medium text-foreground transition-colors hover:bg-[hsl(38_92%_50%/0.15)]"
                    >
                      {cand}
                    </button>
                  ))}
                <button
                  type="button"
                  onClick={() => dismissConfs([c])}
                  className="rounded-full border border-emerald-500/50 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 transition-colors hover:bg-emerald-500/20"
                >
                  Looks right
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Gate status */}
      {blockers.length > 0 && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 px-3 py-2.5 space-y-1">
          <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-destructive">
            <ListChecks className="h-3.5 w-3.5" /> Before you can review
          </p>
          {blockers.slice(0, 6).map((b, i) => (
            <p key={i} className="text-[11px] text-destructive/90">
              • {b}
            </p>
          ))}
          {blockers.length > 6 && (
            <p className="text-[11px] text-destructive/80">…and {blockers.length - 6} more</p>
          )}
        </div>
      )}

      {/* Correction bar — tap fields above, or correct by voice */}
      <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 px-3 py-2.5">
        {recording && recordPurpose === "correct" ? (
          <span className="relative inline-flex flex-shrink-0">
            <span className="voice-mic-ring" />
            <span className="voice-mic-ring" style={{ animationDelay: "0.8s" }} />
            <button
              type="button"
              onClick={stopRecording}
              aria-label="Stop correction"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-red-500 text-white"
            >
              <Square className="h-4 w-4" />
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => startRecording("correct")}
            disabled={processing}
            aria-label="Correct by voice"
            className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand-teal))] text-white transition-transform hover:scale-105 active:scale-95 disabled:opacity-50"
          >
            {processing && recordPurpose === "correct" ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Mic className="h-5 w-5" />
            )}
          </button>
        )}
        {recording && recordPurpose === "correct" && (
          <VoiceWaveform level={micLevel} className="h-5 flex-shrink-0 text-red-500" />
        )}
        <p className="text-[11px] leading-snug text-muted-foreground">
          {recording && recordPurpose === "correct"
            ? `Listening… say the change ("make delivery Thursday", "scratch Dallas") — ${secondsLeft}s`
            : processing
              ? "Atlas is applying your changes…"
              : "Something off? Tap a field above, or tell Atlas the change."}
        </p>
      </div>
    </div>
  );
}

/** Live waveform — bars ride the real mic level (Phase C listening animation). */
const WAVE_PATTERN = [0.55, 0.85, 1, 0.75, 0.5];

function VoiceWaveform({ level, className }: { level: number; className?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-[3px]", className)} aria-hidden>
      {WAVE_PATTERN.map((p, i) => (
        <span
          key={i}
          className="w-[3px] rounded-full bg-current transition-[height] duration-100"
          style={{ height: `${Math.round(3 + level * 14 * p)}px` }}
        />
      ))}
    </span>
  );
}

/** Stop editor — the SAME LocationAutocomplete Type mode uses. */
function StopEditor({
  initial,
  onPick,
}: {
  initial: string;
  onPick: (city: string, state: string) => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <LocationAutocomplete
      value={value}
      onChange={(val: string, pd?: PlaceData) => {
        setValue(val);
        if (pd?.city && pd?.state) onPick(pd.city, pd.state);
      }}
      placeholder="City, ST or ZIP"
      className="h-9 text-xs"
    />
  );
}
